import {NgClass, NgTemplateOutlet} from "@angular/common"
import {ChangeDetectionStrategy, Component, computed, inject, input, model, signal, Signal, WritableSignal} from "@angular/core"
import {takeUntilDestroyed, toSignal} from "@angular/core/rxjs-interop"
import {AccordionStyle, ConfigInfo, InterfaceDescriptor, isConfigInput, UiStyle} from "@cm/template-nodes"
import {ConfigButtonComponent} from "@common/components/viewers/configurator/config-menu/icons-accordion-menu/config-button/config-button.component"
import {ConfigMenuService} from "@common/components/viewers/configurator/config-menu/services/config-menu.service"
import {groupBySectionTitle, SectionEntry} from "@common/components/viewers/configurator/config-menu/descriptor-sections"

export type SectionRenderMode = "full" | "title-only" | "config-only" | "hidden"

type Section = Extract<SectionEntry, {kind: "section"}>
type StepThroughItem = {config: ConfigInfo; sectionTitle?: string}

// Each accordion style is a "mode" that owns its own interaction + visibility logic
abstract class AccordionMode {
    onItemClick(_id: string): void {}
    onSectionClick(_title: string): void {}
    sectionRenderMode(_entry: Section): SectionRenderMode {
        return "full"
    }
    isUngroupedConfigVisible(_id: string): boolean {
        return true
    }
    activeItem(): StepThroughItem | undefined {
        return undefined
    }
    hasPrev(): boolean {
        return false
    }
    hasNext(): boolean {
        return false
    }
    prev(): void {}
    next(): void {}
}

// manualClose: any number of sections/configs open at once. autoClose shares this and only differs
// in what happens when opening (it closes the others) — see AutoCloseMode.
class AccordionListModeBase extends AccordionMode {
    constructor(
        protected readonly selectedIds: WritableSignal<Set<string>>,
        protected readonly openSectionTitles: WritableSignal<Set<string>>,
        protected readonly entries: Signal<SectionEntry[]>,
        protected readonly uiStyle: Signal<UiStyle>,
    ) {
        super()
    }

    override onItemClick(id: string): void {
        const next = new Set(this.selectedIds())
        if (next.has(id)) {
            next.delete(id)
        } else {
            this.onOpenItem(next, id)
            next.add(id)
        }
        this.selectedIds.set(next)
    }

    override onSectionClick(title: string): void {
        const next = new Set(this.openSectionTitles())
        if (next.has(title)) {
            next.delete(title)
        } else {
            this.onOpenSection(next, title)
            next.add(title)
        }
        this.openSectionTitles.set(next)
    }

    override sectionRenderMode(entry: Section): SectionRenderMode {
        if (this.uiStyle() === "icons" || this.openSectionTitles().has(entry.title)) return "full"
        return "title-only"
    }

    // Open hooks — manualClose leaves the other panels untouched; autoClose overrides to close them.
    protected onOpenItem(_next: Set<string>, _id: string): void {}
    protected onOpenSection(_next: Set<string>, _title: string): void {}

    protected findSectionTitleContaining(id: string): string | undefined {
        for (const entry of this.entries()) {
            if (entry.kind === "section" && entry.configs.some((c) => c.props.id === id)) return entry.title
        }
        return undefined
    }
}

class ManualCloseMode extends AccordionListModeBase {}

class AutoCloseMode extends AccordionListModeBase {
    protected override onOpenItem(next: Set<string>, id: string): void {
        next.clear()
        const parentSection = this.findSectionTitleContaining(id)
        this.openSectionTitles.set(parentSection !== undefined ? new Set([parentSection]) : new Set())
    }

    protected override onOpenSection(next: Set<string>, _title: string): void {
        next.clear()
        this.selectedIds.set(new Set())
    }
}

// stepInto: a drill-down — open one section/config at a time and walk back out.
class StepIntoMode extends AccordionMode {
    constructor(
        private readonly selectedIds: WritableSignal<Set<string>>,
        private readonly openSectionTitles: WritableSignal<Set<string>>,
        private readonly entries: Signal<SectionEntry[]>,
    ) {
        super()
    }

    activeId(): string | undefined {
        const ids = this.selectedIds()
        return ids.size > 0 ? ids.values().next().value : undefined
    }

    // Ignores a title whose section no longer exists (e.g. it shrank to a single item after a recompile), so the menu doesn't go blank.
    activeSectionTitle(): string | undefined {
        const titles = this.openSectionTitles()
        const title = titles.size > 0 ? titles.values().next().value : undefined
        return this.entries().some((entry) => entry.kind === "section" && entry.title === title) ? title : undefined
    }

    override onItemClick(id: string): void {
        this.selectedIds.set(this.activeId() === id ? new Set() : new Set([id]))
    }

    override onSectionClick(title: string): void {
        if (this.activeSectionTitle() === title) {
            this.openSectionTitles.set(new Set())
        } else {
            this.openSectionTitles.set(new Set([title]))
        }
        this.selectedIds.set(new Set())
    }

    override sectionRenderMode(entry: Section): SectionRenderMode {
        const activeSection = this.activeSectionTitle()
        const activeConfig = this.activeId()
        if (activeSection === entry.title) return activeConfig ? "config-only" : "full"
        if (!activeSection && !activeConfig) return "title-only"
        if (!activeSection && activeConfig && entry.configs.some((c) => c.props.id === activeConfig)) return "config-only"
        return "hidden"
    }

    override isUngroupedConfigVisible(id: string): boolean {
        const activeSection = this.activeSectionTitle()
        const activeConfig = this.activeId()
        return !activeSection && (!activeConfig || activeConfig === id)
    }
}

// stepThrough: a flat, wizard-style walk over the config groups in on-screen (grouped) order, showing one at a time.
class StepThroughMode extends AccordionMode {
    private readonly items: Signal<StepThroughItem[]>
    private readonly index: Signal<number>

    constructor(
        private readonly selectedIds: WritableSignal<Set<string>>,
        private readonly entries: Signal<SectionEntry[]>,
    ) {
        super()
        this.items = computed<StepThroughItem[]>(() =>
            this.entries().flatMap((entry) =>
                entry.kind === "section" ? entry.configs.map((config) => ({config, sectionTitle: entry.title})) : [{config: entry.config}],
            ),
        )
        this.index = computed<number>(() => {
            const ids = this.selectedIds()
            const activeId = ids.size > 0 ? ids.values().next().value : undefined
            const idx = this.items().findIndex((item) => item.config.props.id === activeId)
            return idx >= 0 ? idx : 0
        })
    }

    override activeItem(): StepThroughItem | undefined {
        return this.items()[this.index()]
    }

    override hasPrev(): boolean {
        return this.index() > 0
    }

    override hasNext(): boolean {
        return this.index() < this.items().length - 1
    }

    override prev(): void {
        const i = this.index()
        if (i > 0) this.setIndex(i - 1)
    }

    override next(): void {
        const i = this.index()
        if (i < this.items().length - 1) this.setIndex(i + 1)
    }

    private setIndex(i: number): void {
        const item = this.items()[i]
        this.selectedIds.set(item ? new Set([item.config.props.id]) : new Set())
    }
}

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-icons-accordion-menu",
    templateUrl: "./icons-accordion-menu.component.html",
    styleUrls: ["./icons-accordion-menu.component.scss"],
    imports: [NgClass, NgTemplateOutlet, ConfigButtonComponent],
})
export class IconsAccordionMenuComponent {
    readonly $useCaptions = input<boolean>(false, {alias: "useCaptions"})
    readonly $tooltipContainer = input.required<HTMLDivElement | undefined>({alias: "tooltipContainer"})
    readonly $interfaceDescriptors = input.required<InterfaceDescriptor<unknown, object>[]>({alias: "interfaceDescriptors"})
    readonly $uiStyle = input.required<UiStyle>({alias: "uiStyle"})
    readonly $accordionStyle = input.required<AccordionStyle>({alias: "accordionStyle"})
    readonly $selectedDescriptorIds = model.required<Set<string>>({alias: "selectedDescriptorIds"})

    readonly configMenuService = inject(ConfigMenuService)
    readonly $iconSize = toSignal(this.configMenuService.iconSize$)
    readonly $useSectionedMenu = toSignal(this.configMenuService.useSectionedMenu$, {initialValue: true})

    readonly $openSectionTitles = signal<Set<string>>(new Set())

    readonly $entries = computed(() => {
        const configs = this.$interfaceDescriptors().filter((desc): desc is ConfigInfo => isConfigInput(desc))
        if (!this.$useSectionedMenu()) return configs.map((config): SectionEntry => ({kind: "item", config}))
        return groupBySectionTitle(configs)
    })

    constructor() {
        this.configMenuService.resetMenu$.pipe(takeUntilDestroyed()).subscribe(() => {
            this.$openSectionTitles.set(new Set())
        })
    }

    private readonly manualCloseMode = new ManualCloseMode(this.$selectedDescriptorIds, this.$openSectionTitles, this.$entries, this.$uiStyle)
    private readonly autoCloseMode = new AutoCloseMode(this.$selectedDescriptorIds, this.$openSectionTitles, this.$entries, this.$uiStyle)
    private readonly stepIntoMode = new StepIntoMode(this.$selectedDescriptorIds, this.$openSectionTitles, this.$entries)
    private readonly stepThroughMode = new StepThroughMode(this.$selectedDescriptorIds, this.$entries)

    readonly $mode = computed<AccordionMode>(() => {
        const ui = this.$uiStyle()
        const style = this.$accordionStyle()
        if (style === "stepThrough" && ui === "accordion") return this.stepThroughMode
        if (style === "stepInto" && ui === "accordion") return this.stepIntoMode
        if (style === "autoClose") return this.autoCloseMode
        return this.manualCloseMode
    })

    readonly $isStepInto = computed(() => this.$mode() === this.stepIntoMode)
    readonly $isStepThrough = computed(() => this.$mode() === this.stepThroughMode)

    readonly $stepIntoActiveId = computed<string | undefined>(() => (this.$mode() === this.stepIntoMode ? this.stepIntoMode.activeId() : undefined))
    readonly $stepIntoActiveSectionTitle = computed<string | undefined>(() =>
        this.$mode() === this.stepIntoMode ? this.stepIntoMode.activeSectionTitle() : undefined,
    )

    readonly $stepThroughActiveItem = computed<StepThroughItem | undefined>(() => this.$mode().activeItem())
    readonly $stepThroughHasPrev = computed(() => this.$mode().hasPrev())
    readonly $stepThroughHasNext = computed(() => this.$mode().hasNext())

    stepThroughPrev() {
        this.$mode().prev()
    }

    stepThroughNext() {
        this.$mode().next()
    }

    sectionRenderMode(entry: Section): SectionRenderMode {
        return this.$mode().sectionRenderMode(entry)
    }

    isUngroupedConfigVisible(id: string): boolean {
        return this.$mode().isUngroupedConfigVisible(id)
    }

    onDescriptorClicked(id: string) {
        this.$mode().onItemClick(id)
    }

    onSectionClicked(title: string) {
        this.$mode().onSectionClick(title)
    }
}
